import React, { useMemo, useRef } from 'react';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import GridTouch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { DashboardNoticeDto } from '../../Dto_Home';

const NOTICE_COLUMN_STATE_KEY = 'home-c-notice-column-state';
const ignoreDashboardGridState = () => undefined;
const formatNoticeDate = (value: string | Date | null | undefined) =>
  value == null ? '' : String(value).slice(0, 10);

interface Props {
  noticeListData: DashboardNoticeDto[];
  selectedNoticeData: DashboardNoticeDto | null;
  onNoticeRowClick: (notice: DashboardNoticeDto) => void;
}

const noticeColumns: ColDef<DashboardNoticeDto>[] = [
  ...getDefaultRowIndexColumnConfig().map((column) => ({ ...column, headerName: 'No' })),
  {
    field: 'writerDt', headerName: '등록일자', width: 110,
    valueFormatter: (params) => formatNoticeDate(params.value),
  },
  { field: 'noticeTitle', headerName: '제목', flex: 1 },
  { field: 'writerEmpNm', headerName: '등록자', width: 100 },
];
const defaultColDef: ColDef = { resizable: true, sortable: true };

const NoticeSection_C: React.FC<Props> = ({
  noticeListData, selectedNoticeData, onNoticeRowClick,
}) => {
  const noticeGridRef = useRef<AgGridReact<DashboardNoticeDto> | null>(null);
  const selectedNoticeNo = selectedNoticeData?.noticeNo;
  const rowClassRules = useMemo(() => ({
    '!bg-blue-100': (params: { data?: DashboardNoticeDto }) =>
      params.data?.noticeNo === selectedNoticeNo,
    'font-semibold': (params: { data?: DashboardNoticeDto }) =>
      params.data?.noticeNo === selectedNoticeNo,
  }), [selectedNoticeNo]);

  const handleNoticeRowClick = (event: RowClickedEvent<DashboardNoticeDto>) => {
    if (event.data) onNoticeRowClick(event.data);
  };

  return (
    <div className="bg-white rounded-lg p-3 shadow-sm grid grid-rows-2 h-full min-h-0">
      <div className="min-h-0 flex flex-col pb-3">
        <h2 className="text-lg font-semibold mb-2">공지사항</h2>
        <div className="ag-theme-alpine flex-1 min-h-0 relative">
          <GridContextWrapper<DashboardNoticeDto>
            gridRef={noticeGridRef}
            paddedGridData={noticeListData}
            baseColumnDefs={noticeColumns}
            setColumnState={ignoreDashboardGridState}
            localStorageKey={NOTICE_COLUMN_STATE_KEY}
            enableCellCalculation
            enableDataHistory={false}
          >
            <GridTouch<DashboardNoticeDto>
              ref={noticeGridRef}
              rowData={noticeListData}
              columnDefs={noticeColumns}
              defaultColDef={defaultColDef}
              rowClassRules={rowClassRules}
              onRowClicked={handleNoticeRowClick}
              setHoveredRowIndex={ignoreDashboardGridState}
              columnStateKey={NOTICE_COLUMN_STATE_KEY}
              setColumnState={ignoreDashboardGridState}
            />
          </GridContextWrapper>
          {noticeListData.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-white pointer-events-none">
              게시 중인 공지사항이 없습니다.
            </div>
          )}
        </div>
      </div>

      <div className="min-h-0 flex flex-col pt-3">
        {selectedNoticeData ? (
          <>
            <div className="font-semibold text-base break-words">
              {selectedNoticeData.topNotice === 'Y' && ' '}
              {selectedNoticeData.noticeTitle}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              등록자: {selectedNoticeData.writerEmpNm || '-'}
            </div>
            <div className="text-sm text-gray-600">
              게시기간: {formatNoticeDate(selectedNoticeData.noticeFromDt)} ~ {formatNoticeDate(selectedNoticeData.noticeEndDt)}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto whitespace-pre-wrap break-words mt-3 border border-gray-300 rounded p-2">
              {selectedNoticeData.noticeComment}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            공지사항을 선택하세요.
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticeSection_C;
